import { getStatusConfig, InvoiceProjectByStatus, User } from 'entities';

import { Column, Collapse, Tag, Row, Link, Dot } from 'components/Blocks';

import { ChevronDownIcon, ChevronUpIcon, WarningIcon } from 'components/Icons';

import { useAppWrapper } from '../../../WrapperContext';

import { useToggle } from 'hooks';

import { getFirstAndLastName } from 'utils/text';
import { sortControls } from './utils';

interface InvoiceTeamUserProps {
  user: User;
  controls: InvoiceProjectByStatus[];
}

export function InvoiceTeamUser({ user, controls }: InvoiceTeamUserProps) {
  const { isOn, toggle } = useToggle();
  const { escapeDrawer } = useAppWrapper();

  const names = getFirstAndLastName(user.name).split(' ');

  const userControls = sortControls(controls);
  const hasWarning = userControls.some((control) => control.addWarning);

  return (
    <Column gap="0">
      <button
        className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-hover"
        disabled={!controls.length}
        onClick={toggle}
      >
        {isOn ? <ChevronUpIcon size={14} /> : <ChevronDownIcon size={14} />}

        <Row className="w-[120px] overflow-hidden text-ellipsis whitespace-nowrap" gap="1">
          {names.map((name, index) => (
            <span
              key={`${name}--${index}`}
              className="relative break-words text-start text-xs font-semibold"
            >
              {name}

              {hasWarning && !!index && (
                <Dot size={8} color="destructive-dark" className="absolute -right-3 top-1" />
              )}
            </span>
          ))}
        </Row>

        <Tag
          name={controls.length}
          color="primary-foreground"
          className="ml-auto w-8 text-primary dark:bg-muted dark:text-muted-foreground"
          size="tiny"
        />
      </button>

      <Collapse expanded={isOn} onlyRenderIfExpanded>
        <Column className="pt-3" gap="3">
          {userControls.map((entry) => (
            <Link
              key={entry.createdAt}
              className="flex items-center pl-2 hover:underline"
              to="projectProfile"
              params={{ projectId: entry.project.id }}
              onClick={escapeDrawer}
            >
              {entry.addWarning ? (
                <WarningIcon
                  size={14}
                  color={getStatusConfig('invoiceControl', entry.status).color}
                />
              ) : (
                <Dot size={12} color={getStatusConfig('invoiceControl', entry.status).color} />
              )}

              <span className="ml-2 break-words text-start text-xs font-semibold">
                {entry.project.name}
              </span>
            </Link>
          ))}
        </Column>
      </Collapse>
    </Column>
  );
}
